/**
 * The iframe player, tamed.
 *
 * Builds the player, decodes its state codes into named events, and polls it
 * for position — the API offers no time event of its own. Everything above
 * this file deals in callbacks rather than YouTube's numbers.
 */

import { loadScript } from '../lib/loaders.ts';
import { loadIframeAPI, type PlayerVars, type YTPlayer, YTState } from './youtube-api.ts';

/** YouTube only accepts a fixed set of playback rates. */
const RATES = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2] as const;

export const nearestRate = (r: number): number =>
  RATES.reduce<number>((a, b) => (Math.abs(b - r) < Math.abs(a - r) ? b : a), 1);

/** The error codes the player reports, in words a listener can act on. */
export const PLAYER_ERRORS: Record<number, string> = {
  2: 'That video id is not valid.',
  5: 'This track cannot be played in an embedded player.',
  100: 'That video is unavailable or private.',
  101: 'The uploader does not allow this track to be embedded.',
  150: 'The uploader does not allow this track to be embedded.',
};

const BOOT_TIMEOUT = 15000;
const POLL_MS = 120;

/** Player vars that keep the card small, unbranded and keyboard-inert. */
const PLAYER_VARS: PlayerVars = {
  playsinline: 1,
  rel: 0,
  modestbranding: 1,
  controls: 0,
  disablekb: 1,
  iv_load_policy: 3,
  fs: 0,
};

export interface PlayerSink {
  playing: (on: boolean) => void;
  ended: () => void;
  buffering: () => void;
  /** Fired when the player has a duration, and again whenever it changes. */
  duration: (seconds: number) => void;
  time: (seconds: number) => void;
  /** The player has finished cueing a video and is ready to start. */
  cued: (player: YTPlayer) => void;
  error: (message: string) => void;
}

export interface PlayerSession {
  player: YTPlayer;
  close: () => void;
}

function decodeState(code: number, player: YTPlayer, sink: PlayerSink): void {
  if (code === YTState.PLAYING) {
    sink.playing(true);
    sink.duration(player.getDuration());
  } else if (code === YTState.PAUSED) {
    sink.playing(false);
  } else if (code === YTState.ENDED) {
    sink.playing(false);
    sink.ended();
  } else if (code === YTState.BUFFERING) {
    sink.buffering();
  } else if (code === YTState.CUED) {
    sink.duration(player.getDuration());
    sink.cued(player);
  }
}

function startPolling(player: YTPlayer, sink: PlayerSink): () => void {
  const id = setInterval(() => {
    sink.time(player.getCurrentTime() || 0);
    sink.duration(player.getDuration() || 0);
  }, POLL_MS);
  return () => {
    clearInterval(id);
  };
}

/** Builds the iframe player and resolves once it reports ready. */
export async function openSession(mountId: string, sink: PlayerSink): Promise<PlayerSession> {
  const YT = await loadIframeAPI(loadScript);
  return new Promise<PlayerSession>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('YouTube player timed out'));
    }, BOOT_TIMEOUT);

    const player: YTPlayer = new YT.Player(mountId, {
      height: '140',
      width: '248',
      playerVars: { ...PLAYER_VARS, origin: location.origin },
      events: {
        onReady: () => {
          clearTimeout(timer);
          const stop = startPolling(player, sink);
          resolve({
            player,
            close: () => {
              stop();
              player.destroy();
            },
          });
        },
        onStateChange: (e) => {
          decodeState(e.data, player, sink);
        },
        onError: (e) => {
          sink.error(PLAYER_ERRORS[e.data] ?? 'YouTube could not play this track.');
        },
      },
    });
  });
}
